import { useState, useEffect, useRef, useMemo } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "../../Components/UI/dialog"
import { Button } from "../../Components/UI/button"
import { Input } from "../../Components/UI/input"
import { Textarea } from "../../Components/UI/textarea"
import { MultiSelect } from "../../Components/UI/multi-select"
import { Checkbox } from "../../Components/UI/checkbox"
import { Label } from "../../Components/UI/label"
import { TimeInputWithDialog } from "../../Components/Common/TimePickerWatch"
import CustomSearchableDropdown from "../../Components/Common/CustomSearchableDD"
import { DatePicker } from "../../Components/Common/DatePicker"
import ConnectionEntryDialog from "../Connections/ConnectionEntry"
import { getUsersArr, userIsAdmin, getContractsArr, getAgencyDetails, checkIfInqOnlyMode, isSmartSellerMode } from "../../Store/Reducers"
import { manageRecordAPI } from "./RecordAPI"
import { useSelector } from "react-redux"
import { getAuthData } from '../../Utils/AuthUtils'
import { getConfigData } from '../../Utils/Storage'
import { capitalizeFirstLetter, combineDateTime, currentUserHasPermission, getConnectionOptionsWithSearch, getRelativeDateText, getThreadTypeMeta, isPendingTasks } from "../../Utils/Utilities"
import { X, ArrowLeft, Check, Ban, Trash2, ArrowUpRight, SquareArrowOutUpRight, Phone, MessageCircle, Handshake, MapPin, MoreHorizontal } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../Components/UI/tooltip"
import { BsWhatsapp } from "react-icons/bs"
import { toast } from "sonner"
import { AlertBox } from "../../Components/Common/AlertBox"
import { format } from "date-fns"
import MastersDialog from "../Settings/Masters"
import { getThreadTypesArr } from "../../Store/Reducers"
import { getConnectionByID } from "../../Utils/IndexedDBUtils"
import TaskEntry from "../Tasks/TaskEntry"
import { taskActionAPI } from "../Tasks/TasksAPI"
import { se } from "date-fns/locale"
import { getMobileHeaderTitle } from "../../Utils/Utilities"
import useIsMobile from "../../Hooks/useIsMobile"
import { useUserConnectionFilter } from "../../Hooks/useUserConnectionFilter"
import { getThreadModeTitle } from "../../Utils/Utilities"
import { getActiveThreadTypesArr } from "../../Store/Reducers"
import ThreadEntry from "../Threads/ThreadEntry"
import { FaCircle } from "react-icons/fa6"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSackDollar } from "@fortawesome/free-solid-svg-icons"
import ConnectionDetailsWrapper from "../Connections/ConnectionDetailsWrapper"
import { SpeechControls } from "../../Components/Common/SpeechControls"

export default function RecordEntry({
	triggerButton = true,
	open: externalOpen,
	onOpenChange: externalOnOpenChange,
	prefilledData = {},
	onRecordAdded,
	selectedTaskForRecord = null,
	onCloseThread,
	showUpdateStatus: showUpdateStatus = false,
	shouldOpenTaskEntry = false
}) {
	const nowDT = new Date();
	const defaultDT = format(nowDT, "dd-MMM-yy");
	const defaultTime = format(nowDT, "HH:mm");
	const isMobile = useIsMobile();

	// Resolve the time for pre-filled/edit records (RecDT is stored as a full datetime, e.g. 2026-09-26 15:40:00)
	const getPrefilledRecordTime = () => {
		if (prefilledData?.recordTime) return prefilledData.recordTime;
		const parsed = prefilledData?.recordDate ? new Date(prefilledData.recordDate) : null;
		return parsed && !isNaN(parsed.getTime()) ? format(parsed, "HH:mm") : defaultTime;
	};

	// Local state variables
	const [internalOpen, setInternalOpen] = useState(false);
	const [contracts, setContracts] = useState(prefilledData?.contracts || []);
	const open = externalOpen !== undefined ? externalOpen : internalOpen;
	const setOpen = externalOnOpenChange !== undefined ? externalOnOpenChange : setInternalOpen;
	const [connectionDialogOpen, setConnectionDialogOpen] = useState(false);
	const [connectionSearchText, setConnectionSearchText] = useState('');
	const [recordTypeDialogOpen, setRecordTypeDialogOpen] = useState(false);
	const [showDropdownRecordType, setShowDropdownRecordType] = useState(false);
	const [autoOpenDropdownRecordType, setAutoOpenDropdownRecordType] = useState(false);
	const [filteredThreads, setFilteredThreads] = useState([]);
	const authData = getAuthData();
	const [taskPrefilledData, setTaskPrefilledData] = useState({});
	const [isThreadSelectDisabled, setIsThreadSelectDisable] = useState(false);
	const [threadSelectPlaceholder, setThreadSelectPlaceholder] = useState("Select Threads");
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const originalNotesRef = useRef(null);
	const threadTypesData = useSelector(getThreadTypesArr);
	const recordTypesData = useSelector((state) => state.masterData.recordTypes);
	const [isNextAction, setIsNextAction] = useState(false);
	const [showLocation, setShowLocation] = useState(false);
	const [showColdDatePicker, setShowColdDatePicker] = useState(false);
	const canAddConnections = currentUserHasPermission('ConnEntry');
	const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
	const isEditMode = prefilledData?.isEditMode || false;
	const recordTypes = recordTypesData.map((recordType) => {
		return {
			label: recordType.Typ, value: recordType.Typ
		}
	})
	const [recordFormData, setRecordFormData] = useState({
		connID: 0,
		threadIDs: [],
		recordDate: defaultDT,
		recordTime: defaultTime,
		recordType: capitalizeFirstLetter(selectedTaskForRecord?.taskType) || '',
		notes: selectedTaskForRecord ? `Completed: ${selectedTaskForRecord?.taskName}` : "",
		pocUserID: authData.UserID,
		isLocationReq: false,
		contractIDs: prefilledData.contractIDs || [],
		currStageID: null,
		reason: '',
		secConnIDs: prefilledData.secConnIDs || [],
		recordID: prefilledData?.recordID || null,
		isMarkCold: false,
		markColdDT: ''
	});
	const formRef = useRef();

	// Data which is coming from store (redux)
	const users = useSelector(getUsersArr);
	const contractsFromRedux = useSelector(getContractsArr);
	const { data: threadData } = useSelector((state) => state.threadData);
	const { data: connectionData } = useSelector((state) => state.connectionData);
	const isAdmin = useSelector(userIsAdmin);
	const isSmartSeller = useSelector(isSmartSellerMode);
	const activeThreadTypesArr = useSelector(getActiveThreadTypesArr);
	const currActiveThreadType = getThreadModeTitle(activeThreadTypesArr);
	const agencyData = useSelector(getAgencyDetails);
	const showModule = { contracts: agencyData?.EnblContracts };
	const masterData = useSelector((state) => state.masterData)
	const allDisqlRsns = useSelector((state) => state.masterData.disqlReasons) || []
	const [isUpdateStatus, setIsUpdateStatus] = useState(false);
	const threadStages = useSelector((state) => state.masterData.threadStages);
	const [currentThreadStages, setCurrentThreadStages] = useState([]);
	const [currentThreadType, setCurrentThreadType] = useState(null);
	const [currentStageID, setCurrentStageID] = useState(null);
	const [currentThreadID, setCurrentThreadID] = useState(null);
	const [currentThread, setCurrentThread] = useState({});
	const [isThreadEntryOpen, setIsThreadEntryOpen] = useState(false);
	const [convertToSalePrefill, setConvertToSalePrefill] = useState(null);
	const [isDetailOpen, setIsDetailOpen] = useState(false);
	const [secondaryContacts, setSecondaryContacts] = useState([]);
	const [isTaskEntryOpenAfterSave, setIsTaskEntryOpenAfterSave] = useState(false);
	const allowSecConns = useSelector(state => state.masterData.allowSecConns);
	const allowLinkedThreads = false;
	const allowLinkedCtrcs = false;
	const stageColors = ["#062f12", "#094418", "#0b5d1e", "#107a28", "#17a53b"];
	const [availableThreadOptions, setAvailableThreadOptions] = useState([]);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

	const disqlRsns = useMemo(() => {
		if (!allDisqlRsns || allDisqlRsns.length === 0 || !currentThreadType) return []

		try {
			// Find the reason object for current thread type
			const currentThreadReasons = allDisqlRsns.find(item => item.TID === currentThreadType)

			if (!currentThreadReasons || !currentThreadReasons.Rsns) return []

			// Parse the Rsns JSON string to get the array of reasons
			const reasons = JSON.parse(currentThreadReasons.Rsns)
			return reasons.map(reason => ({
				value: reason.R,
				label: reason.R,
			}))
		} catch (error) {
			console.error("Error parsing disqualification reasons:", error)
			return []
		}
	}, [currentThreadType, recordFormData.threadIDs])

	const rejectionReasonOptions = useMemo(() => {
		if (!threadData || !masterData?.rejectionReasons || !currentThreadType) return []

		const threadReasons = masterData.rejectionReasons.find(item => item.TID === currentThreadType)

		if (!threadReasons) return []

		try {
			const reasons = JSON.parse(threadReasons.Rsns)
			return reasons.map(reason => ({
				value: reason.R,
				label: reason.R,
			}))
		} catch (error) {
			console.error("Error parsing failure reasons:", error)
			return []
		}
	}, [currentThreadType, recordFormData.threadIDs])

	useEffect(() => {
		const ids = recordFormData.threadIDs || [];

		if (ids.length === 1) {
			const threadID = ids[0];
			setCurrentThreadID(threadID);
			const thread = threadData?.find(t => t.ThreadID == threadID);
			setCurrentThread(thread);
			setCurrentThreadType(thread?.TypeID);
			setCurrentStageID(thread?.CurrStageID)
			const thrStages = threadStages?.find(t => t.TID === thread?.TypeID)?.Stages || [];
			setCurrentThreadStages(thrStages);

			const secConnIDs = thread?.SecConnIDs?.split(",").map(id => Number(id)).filter(Boolean) || [];
			const secContacts = connectionData.filter(conn => secConnIDs.includes(conn.ConnID)).map((c) => {
				return {
					value: c.ConnID.toString(), // Ensure it's a string for MultiSelect
					label: !c.BizName || c.BizName.trim() === '-' ? c.ContactName : c.BizName
				}
			}).sort((a, b) => a.label.localeCompare(b.label))
			setSecondaryContacts(secContacts);
			setRecordFormData(prev => {
				const currentSec = prev.secConnIDs || [];
				const filteredSec = currentSec.filter(id => secConnIDs.map(String).includes(id));
				if (filteredSec.length === currentSec.length && filteredSec.every((v, i) => v === currentSec[i])) {
					return prev;
				}
				return {
					...prev,
					secConnIDs: filteredSec
				};
			});
		} else {
			setIsUpdateStatus(false);
			setCurrentThreadStages([]);
			setCurrentThread(null);
			setCurrentThreadType(null);
			setCurrentStageID(null);
			setCurrentThreadID(null);
			setSecondaryContacts([]);
			setRecordFormData(prev => {
				if (prev.currStageID === null && (!prev.secConnIDs || prev.secConnIDs.length === 0)) {
					return prev;
				}
				return { ...prev, currStageID: null, secConnIDs: [] };
			});
		}
	}, [(recordFormData.threadIDs || []).join(','), threadData, threadStages]);

	const getCurrentThreadStages = () => {
		const baseStages = currentThreadStages
			.map((stage, index) => ({
				value: stage.SID,
				label: stage.S,
				icon: (
					<FaCircle
						className="text-xs inline"
						style={{ color: stageColors[index % stageColors.length] }}
					/>
				)
			}))
			.filter(opt => opt.value !== currentStageID);

		let extra = [];

		if (isNextAction) {
			return [...baseStages]
		}

		switch (currentThreadType) {
			case 1:
			case 4:
				extra = [
					{
						value: -3,
						label: "Success (Convert to Sale)",
						icon: <FontAwesomeIcon icon={faSackDollar} className="w-4 h-4 lg:w-5 lg:h-5 text-sahi-green" />
					},
					{
						value: -1,
						label: currentStageID === 1
							? "Mark Fake"
							: "Mark Rejected",
						icon: currentStageID === 1 ? <Ban className="text-sahi-red w-4 h-4 lg:w-5 lg:h-5" /> : <X className="text-sahi-red w-4 h-4 lg:w-5 lg:h-5" />
					}
				];
				break;

			case 2:
			case 5:
				extra = [
					{ value: -2, label: "Complete Sale" },
					{ value: -1, label: "Cancel Sale" },
				];
				break;

			case 3:
			case 6:
				extra = [
					{ value: -2, label: "Close Ticket" },
					{ value: -1, label: "Cancel Ticket" },
				];
				break;

			default:
				extra = [];
		}

		const all = [...baseStages, ...extra];

		const markFake = all.find(x => x.label === "Mark Fake");
		const done = all.find(x => x.value === -2 || x.value === -3);
		const cancel = all.find(x => x.value === -1);
		const rest = all.filter(x => x !== markFake && x !== done && x !== cancel);

		let ordered;

		if (markFake) {
			ordered = [markFake, ...rest, done].filter(Boolean);
		} else {
			ordered = [...rest, cancel, done].filter(Boolean);
		}

		return ordered;
	};


	const getReasonOptions = () => {
		if ([1, 2, 3].includes(currentThreadType) && currentStageID === 1) {
			return disqlRsns;
		}
		return rejectionReasonOptions;
	};

	const getReasonLabel = () => {
		if ([1, 2, 3].includes(currentThreadType) && currentStageID === 1) {
			return 'Disqualification Reason';
		}
		return 'Failure Reason';
	}

	// Filter users based on selected connection
	const filteredUsers = useUserConnectionFilter(recordFormData.connID, connectionData);

	// Ask for geo location on condition
	useEffect(() => {
		let recordData = recordTypesData.find(recordType => recordType.Typ == recordFormData.recordType);
		if (recordData) {
			setShowLocation(recordData.HasGeo);
		}
	}, [recordFormData.recordType])

	// Trigger handleThreadSelectToggle function using useEffect  
	useEffect(() => {
		if (open) {
			handleThreadSelectToggle();
			setIsNextAction(false);
			setIsUpdateStatus(false);
		}
	}, [recordFormData.connID, open]);

	useEffect(() => {
		// Reset states when the dialog closes
		if (!open) {
			setShowLocation(false);
			setIsNextAction(false);
			setIsUpdateStatus(false);
			setIsInitialLoad(true);
			setShowDropdownRecordType(false);
			setAutoOpenDropdownRecordType(false);
			setCurrentThreadStages([]);
			setCurrentThreadType(null);
			setCurrentStageID(null);
			setCurrentThreadID(null);
			setCurrentThread({});
			setSecondaryContacts([]);
			setIsDeleteDialogOpen(false);
			setShowColdDatePicker(false);
			setRecordFormData(prev => {
				if (prev.connID === 0 && (!prev.threadIDs || prev.threadIDs.length === 0) && prev.recordType === '' && prev.notes === '') {
					return prev;
				}
				return {
					connID: 0,
					threadIDs: [],
					recordDate: format(new Date(), "dd-MMM-yy"),
					recordTime: format(new Date(), "HH:mm"),
					recordType: '',
					notes: "",
					pocUserID: authData?.UserID,
					isLocationReq: false,
					contractIDs: [],
					currStageID: null,
					reason: '',
					secConnIDs: [],
					recordID: null,
					isMarkCold: false,
					markColdDT: ''
				};
			});
		}
	}, [open]);

	useEffect(() => {
		// Only initialize the form on the first render after the dialog opens
		if (open && isInitialLoad) {
			if (isEditMode && prefilledData) {
				// Edit mode: Use the prefilled data from record
				originalNotesRef.current = prefilledData.notes || "";
				setRecordFormData({
					connID: prefilledData.connID || 0,
					threadIDs: prefilledData.threadIDs || [],
					recordDate: prefilledData.recordDate ? format(new Date(prefilledData.recordDate), "dd-MMM-yy") : defaultDT,
					recordTime: getPrefilledRecordTime(),
					recordType: prefilledData.recordType || '',
					notes: prefilledData.notes || "",
					pocUserID: prefilledData.pocUserID || authData?.UserID,
					isLocationReq: false,
					contractIDs: prefilledData.contractIDs || [],
					currStageID: null,
					reason: '',
					secConnIDs: prefilledData.secConnIDs || [],
					recordID: prefilledData.recordID || null
				});
			} else {
				// Add mode: Use prefilledData if provided
				let recordType = prefilledData?.recordType || '';
				if (selectedTaskForRecord?.taskType) {
					const matchingType = (recordTypesData || []).find(
						rt => rt.Typ.toLowerCase() === selectedTaskForRecord.taskType.toLowerCase()
					);
					recordType = matchingType ? matchingType.Typ : capitalizeFirstLetter(selectedTaskForRecord.taskType);
				}

				setRecordFormData({
					connID: prefilledData?.connID || 0,
					threadIDs: prefilledData?.threadIDs || [],
					recordDate: prefilledData?.recordDate ? format(new Date(prefilledData.recordDate), "dd-MMM-yy") : defaultDT,
					recordTime: getPrefilledRecordTime(),
					recordType: recordType,
					notes: selectedTaskForRecord ? `Completed: ${selectedTaskForRecord?.taskName}` : (prefilledData?.notes || ""),
					pocUserID: prefilledData?.pocUserID || authData?.UserID,
					isLocationReq: false,
					contractIDs: prefilledData?.contractIDs || [],
					secConnIDs: prefilledData?.secConnIDs || []
				});
			}

			if (!prefilledData?.connID) {
				setFilteredThreads([]);
				setIsThreadSelectDisable(true);
				setThreadSelectPlaceholder("Please Select Contact");
				setContracts(contracts);
			}

			setIsInitialLoad(false); // Prevent re-initialization on subsequent renders
		}
	}, [open, isInitialLoad, isEditMode, selectedTaskForRecord?.taskType, selectedTaskForRecord?.taskName, prefilledData?.connID, prefilledData?.recordID, prefilledData?.recordType]);

	useEffect(() => {
		if (isNextAction) {
			setTaskPrefilledData({
				threads: recordFormData.threadIDs && recordFormData.threadIDs.length > 0 ? recordFormData.threadIDs : '',
				connID: recordFormData.connID,
				// taskType: recordFormData.recordType,
				contractIDs: recordFormData.contractIDs || [],
				connectionLocked: true,
				taskLabel: 'Next Task',
				hideDescription: true,
				descriptionLabel: 'Task Description',
				typeLabel: 'Type',
				pocUserLabel: 'Assigned to',
				threadsLabel: `Linked ${currActiveThreadType.typPlr}`,
				contractsLabel: 'Linked Contracts',
				secConnIDs: recordFormData.secConnIDs || [],
			});
		}
	}, [isNextAction, recordFormData.connID, recordFormData.threadIDs, recordFormData.recordType, recordFormData.contractIDs, currActiveThreadType.typPlr])

	useEffect(() => {
		if (!isUpdateStatus) {
			setRecordFormData((prev) => {
				if (prev.currStageID === null) return prev;
				return { ...prev, currStageID: null };
			});
		}
	}, [isUpdateStatus])

	useEffect(() => {
		const ALLOWED_TYPES = ['Call', 'Message', 'Meeting', 'Visit'];
		const isAllowed = ALLOWED_TYPES.includes(recordFormData.recordType);
		// If the pre-filled or selected type isn't a standard icon, show the dropdown view
		if (!isAllowed && recordFormData.recordType) {
			setShowDropdownRecordType(true);
		}
	}, [recordFormData.recordType]);

	useEffect(() => {
		// Reset task entry flag when dialog closes
		if (!open) {
			setIsTaskEntryOpenAfterSave(false);
		}
	}, [open]);

	// Function for manage thread multiselect data
	const handleThreadSelectToggle = async () => {
		const prefilledConnID = recordFormData.connID || 0;
		let matchedThreads = [];
		let contracts = [];

		// Reset form
		if (prefilledData?.threads && prefilledData?.threads.length > 0 && prefilledData?.threadsLocked) {
			setIsThreadSelectDisable(true);
			setThreadSelectPlaceholder("Threads are locked for this record");
			return;
		}

		try {
			const connectionData = await getConnectionByID(prefilledConnID);
			const openThreadIDsArr = connectionData?.OpenThreadIDs?.split(",") || [];
			const contractIDs = connectionData?.CtrcIDs?.split(",") || [];
			threadData.forEach((thread) => {
				if (openThreadIDsArr.includes(String(thread.ThreadID))) {
					const sinceDT = thread.SinceDT ? getRelativeDateText(new Date(thread.SinceDT), false, false) : '';
					const targetDT = thread.DueDT ? getRelativeDateText(new Date(thread.DueDT), false, false) : '';
					let selThreadTypeInfo = getThreadTypeMeta(thread.TypeID, threadTypesData);
					let formattedThread = {
						value: String(thread.ThreadID),
						threadType: selThreadTypeInfo.nameSng,
						product: thread.MainProduct,
						label: `${!isInqOnlyMode ? selThreadTypeInfo.nameSng + ' - ' : ''}${thread.MainProduct} (${sinceDT})`,
						icon: selThreadTypeInfo.Icon,
						color: selThreadTypeInfo.colorClass,
						typeID: thread.TypeID,
						sinceDT: sinceDT,
						targetDT: targetDT
					};
					matchedThreads.push(formattedThread);
				}
			})

			if (matchedThreads.length === 1) {
				const singleThreadID = [matchedThreads[0].value];
				setRecordFormData(prev => {
					if (prev.threadIDs?.length === 1 && prev.threadIDs[0] === singleThreadID[0] && prev.currStageID === null && prev.reason === '') {
						return prev;
					}
					return {
						...prev,
						threadIDs: singleThreadID,
						currStageID: null,
						reason: ''
					};
				});
				// Disable the multi-select/dropdown for linking thread if it's auto-selected
				setIsThreadSelectDisable(true);
				setThreadSelectPlaceholder("Thread is automatically selected.");
			} else if (matchedThreads.length === 0) {
				// No threads found for this contact - disable both Update Status and MultiSelect
				setIsThreadSelectDisable(true);
				setThreadSelectPlaceholder("No threads found for this contact.");
				setIsUpdateStatus(false); // Cannot update status if no thread is linked
				setRecordFormData(prev => {
					if ((!prev.threadIDs || prev.threadIDs.length === 0) && prev.currStageID === null && prev.reason === '') {
						return prev;
					}
					return { ...prev, threadIDs: [], currStageID: null, reason: '' };
				});
			} else {
				// Multiple threads available - enable MultiSelect/Dropdown (if allowed)
				setIsThreadSelectDisable(false);
				setThreadSelectPlaceholder("Select Threads");
				// Clear current thread selection in case a new connection was selected with multiple threads
				setRecordFormData(prev => {
					if ((!prev.threadIDs || prev.threadIDs.length === 0) && prev.currStageID === null && prev.reason === '') {
						return prev;
					}
					return { ...prev, threadIDs: [], currStageID: null, reason: '' };
				});
			}

			contracts = contractsFromRedux.filter(contract => contract.ContractID && contractIDs.includes(String(contract.ContractID)))
			setContracts(contracts);
			setFilteredThreads(matchedThreads);
			setAvailableThreadOptions(matchedThreads);
		} catch (error) {
			console.error(`Error while fetching data from IndexedDB`, error);
			toast.error("Something went wrong while loading thread data. Please contact the SahiClub support team.");
		}
	};


	// Handling onChange event and date/time validations
	const handleFieldChange = (fieldName, value) => {
		const allowedFields = [
			"connID",
			"threadIDs",
			"recordDate",
			"recordTime",
			"recordType",
			"notes",
			"pocUserID",
			"isLocationReq",
			"contractIDs",
			"currStageID",
			"reason",
			"secConnIDs",
			"isMarkCold",
			"markColdDT"
		];

		if (fieldName == "currStageID" && (value == -1 || value == -2 || value == -3)) {
			const threadID = recordFormData.threadIDs[0]
			setIsNextAction(false)
			// if (isPendingTasks(threadID)) return;
		} else if (fieldName == "currStageID") {
			setRecordFormData(prev => ({ ...prev, reason: '' }))
		}

		if (fieldName === "threadIDs") {
			const newThreadIDs = Array.isArray(value) ? value : [];
			if (newThreadIDs.length !== 1) {
				// Disable status update and clear stage/reason if not exactly one thread is selected
				setIsUpdateStatus(false);
				setRecordFormData(prev => ({ ...prev, currStageID: null, reason: '' }))
			}
		}

		if (!allowedFields.includes(fieldName)) {
			console.warn(`Invalid field name: ${fieldName}`);
			return;
		}

		// Handling for date field validation
		if (fieldName === "recordDate") {
			// Validate the date
			const resultDT = new Date(value + " 23:59:59");
			if (resultDT > nowDT) {
				const errorMessage = "Invalid Date";
				const description = "Please select a present or past date. Future date is not allowed.";
				toast.error(errorMessage);
				setRecordFormData((prev) => ({ ...prev, recordDate: defaultDT }));
				return;
			}
		}

		// Handling for recordType validation
		if (fieldName === "recordType" && !value) {
			toast.error("Please select a type.");
			return;
		}

		// Handling for time field validation (compare the combined date + time)
		if (fieldName === "recordTime" && value) {
			const resultDT = new Date((recordFormData.recordDate || defaultDT) + " " + value);
			if (!isNaN(resultDT.getTime()) && resultDT > new Date()) {
				toast.error("Invalid Time", { description: "Please select a present or past time. Future time is not allowed." });
				return;
			}
		}
		// Update the field if validation passed or it's not a date/time field
		setRecordFormData((prev) => ({ ...prev, [fieldName]: value }));
	}

	// Handle every search select fields
	const handleSearchSelectChange = (fieldName, option, value, searchText) => {
		if (value === "add") {
			if (fieldName === "connID") {
				setConnectionSearchText(searchText || '');
				// Close current dialog and open connection dialog
				setOpen(false);
				setConnectionDialogOpen(true);
			} else if (fieldName === "recordType") {
				//open recordtype dialog without closing parent
				setRecordTypeDialogOpen(true);
			}
		} else {
			setRecordFormData((prev) => ({ ...prev, [fieldName]: option?.value }));
		}
	}

	const handleRecordTypeAdded = (newRecordTypeName) => {
		if (newRecordTypeName) {
			setRecordFormData((prev) => ({
				...prev,
				recordType: newRecordTypeName
			}));
		}
		setRecordTypeDialogOpen(false);
	}

	// Get Formatted Array For Components
	const getFormatedArray = (fieldID) => {
		let searchOptions = [];
		if (fieldID === "connID") {
			searchOptions = getConnectionOptionsWithSearch(connectionData);
		} else if (fieldID === "pocUserID") {
			searchOptions = filteredUsers.map((user) => ({
				value: user?.userID || "",
				label: user.name || ""
			}));
		}
		else if (fieldID === "ContractIDs") {
			searchOptions = contracts.map((contract) => ({
				value: contract.ContractID || "",
				label: `#${contract.ContractNo} - ${contract.ContractType} (${contract.Product})`
			}));
		}
		return searchOptions;
	}

	// Get user's current latitude and longitude
	const getLatLon = () => {
		return new Promise((resolve, reject) => {
			if (!navigator.geolocation) {
				reject("Geolocation not supported.");
				return;
			}
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude } = position.coords;
					resolve([latitude, longitude]);
				},
				(error) => {
					if (error.code === error.PERMISSION_DENIED) {
						reject(
							"Location access was denied. Please enable location permission for this site in your browser settings."
						);
					} else if (error.code === error.TIMEOUT) {
						reject("Location request timed out. Please try again.");
					} else {
						reject(error.message);
					}
					reject(error);
				},
				{ timeout: 10000 },
			);
		})
	}

	// Function for handling form submission
	const handleSaveRecord = async () => {
		// For edit mode, validate recordType and notes
		if (isEditMode) {
			if (!recordFormData.recordType || recordFormData.recordType.trim() === "") {
				toast.error("Please select a type.");
				return;
			}
			if (!recordFormData.notes || recordFormData.notes.trim() === "") {
				toast.error("Please enter notes for this record.");
				return;
			}

			const notesLower = recordFormData.notes.trim().toLowerCase();
			const originalNotesLower = (originalNotesRef.current || "").trim().toLowerCase();
			const isAutoGeneratedNote = originalNotesLower === "sent message." || originalNotesLower === "dialed call.";

			if (isAutoGeneratedNote && notesLower === originalNotesLower && !isNextAction) {
				setOpen(false);
				return;
			}

			try {
				// NOTE: When isNextAction is true in edit mode, collect task data from TaskEntry form
				let taskData = null;

				if (isNextAction && !shouldOpenTaskEntry) {
					const extTaskData = formRef.current?.getExtTaskData();
					if (extTaskData?.validation && !extTaskData?.validation.valid) {
						toast.error(extTaskData?.validation.error.title);
						return;
					}
					taskData = Object.keys(extTaskData?.data || {}).length > 0 ? extTaskData?.data : null;
				}

				let formattedRecordData = {
					recID: recordFormData.recordID,
					connID: recordFormData.connID,
					pocUserID: recordFormData.pocUserID,
					recNotes: recordFormData.notes || "",
					recDT: new Date(),
					recType: recordFormData.recordType,
					recThreadIDs: '',
					recContractIDs: '',
					recSecConnIDs: '',
					recLat: '',
					recLon: '',
					currStageID: 1,
					reason: ''
				};
				// NOTE: Always use recChangeTypeID = 2 (RECEDIT). Backend will check if task data exists and handle task creation
				const result = await manageRecordAPI(formattedRecordData, taskData, 'EDIT', 2);

				if (!result.error) {
					setOpen(false);
					toast.success("Record updated successfully.");
					if (onRecordAdded) {
						onRecordAdded(); // Pass no data for edits
					}
					return;
				}
				toast.error(result.message);
			} catch (error) {
				toast.error(error.message);
				console.error("Error updating record:", error);
			}
			return;
		}

		// Original validation for ADD mode
		const requiredFields = [
			{ field: "connID", label: "Contact", message: "Please select a contact." },
			{ field: "notes", label: "Notes", message: "Please enter notes for this record." },
			{ field: "recordType", label: "Type", message: "Please select a type." },
			{ field: "recordDate", label: "Record Date", message: "Please select a record date." },
			{ field: "pocUserID", label: "Assigned To", message: "Please assign this record to someone." }
		];

		if (isUpdateStatus && availableThreadOptions.length > 1 && recordFormData.threadIDs.length === 0) {
			toast.error(`Please select a ${isInqOnlyMode ? 'Inquiry' : 'Thread'} to update its status.`);
			return;
		}

		if (isUpdateStatus) {
			if (recordFormData.currStageID === null || recordFormData.currStageID === undefined || recordFormData.currStageID === '') {
				toast.error("Please select a status.");
				return;
			}
			requiredFields.push({
				field: "currStageID",
				label: "Status",
				message: "Please select status."
			});
		}
		if (isUpdateStatus && (recordFormData.currStageID === -1)) {
			requiredFields.push({
				field: "reason",
				label: "Reason",
				message: "Please select reason."
			});
		}

		// If shouldOpenTaskEntry is true and Add Next Task is checked, don't validate task fields yet
		if (shouldOpenTaskEntry && isNextAction) {
			// Just let the record be saved, task entry will open after
			// Don't validate task-specific fields here
		}

		for (const { field, label, message } of requiredFields) {
			const value = recordFormData[field];
			const isEmpty = typeof value === "string" ? value.trim() === "" : !value;
			if (isEmpty) {
				toast.error(`Please select a ${label}.`);
				return;
			};
		}

		if (recordFormData.isLocationReq) {
			try {
				const [lat, lon] = await getLatLon()
				recordFormData.recLat = lat
				recordFormData.recLon = lon
			} catch (error) {
				toast.error("Failed to get location");
				return;
			}
		}

		try {
			// Handle Task Submit If Next Action is Selected
			let data = {};
			if (isNextAction && !shouldOpenTaskEntry) {
				data = formRef.current?.getExtTaskData();
				if (data?.validation && !data?.validation.valid) {
					toast.error(data?.validation.error.title);
					return;
				}
			}
			let taskData = Object.keys(data).length != 0 ? data?.data : {};
			let dateStr = recordFormData.recordDate;
			if (dateStr) {
				const parts = dateStr.split("-");
				if (parts.length === 3 && parts[2].length === 2) {
					parts[2] = "20" + parts[2];
					dateStr = parts.join("-");
				}
			}
			let parsedDate = new Date(dateStr + " " + (recordFormData.recordTime || defaultTime));
			if (isNaN(parsedDate.getTime())) {
				parsedDate = new Date();
			}
			let formattedRecordData = {
				connID: recordFormData.connID || 0,
				pocUserID: recordFormData.pocUserID || 0,
				recType: recordFormData.recordType ? recordFormData.recordType : "",
				recDT: parsedDate.toISOString(),
				recNotes: recordFormData.notes || "",
				recThreadIDs: (recordFormData.threadIDs || []).join(",") == "" ? (prefilledData?.threadIDs || []).join(",") : (recordFormData.threadIDs || []).join(","),
				recContractIDs: (recordFormData.contractIDs || []).join(","),
				recLat: recordFormData.recLat,
				recLon: recordFormData.recLon,
				currStageID: isUpdateStatus ? recordFormData.currStageID : undefined,
				reason: isUpdateStatus ? recordFormData.reason : '',
				recSecConnIDs: (recordFormData.secConnIDs || []).join(","),
				isMarkCold: recordFormData.isMarkCold || false
			};
			let result;

			// When shouldOpenTaskEntry is true and user checked Add Next Task, save only the record
			if (shouldOpenTaskEntry && isNextAction) {
				result = await manageRecordAPI(formattedRecordData, null, 'ADD', 0);
			} else if (selectedTaskForRecord) {
				// Completing an existing task with a record
				if (isUpdateStatus) {
					result = await manageRecordAPI(formattedRecordData, selectedTaskForRecord, 'ADD', 5);
				} else {
					result = await manageRecordAPI(formattedRecordData, selectedTaskForRecord, 'ADD', recordFormData?.recordID ? 2 : 5);
				}
			} else if (!isUpdateStatus && Object.keys(taskData).length != 0) {
				// Pass taskData instead of null
				result = await manageRecordAPI(formattedRecordData, taskData, 'ADD', 1);
			} else if (isUpdateStatus && Object.keys(taskData).length == 0) {
				result = await manageRecordAPI(formattedRecordData, null, 'ADD', 3);
			} else if (isUpdateStatus && Object.keys(taskData).length != 0) {
				// Pass taskData instead of null
				result = await manageRecordAPI(formattedRecordData, taskData, 'ADD', 4);
			} else {
				// Adding just a record
				result = await manageRecordAPI(formattedRecordData, null, 'ADD', 0);
			}

			if (!result.error) {
				// If shouldOpenTaskEntry is true and Add Next Task is checked, open task entry instead of closing
				if (shouldOpenTaskEntry && isNextAction) {
					toast.success("Record added.");
					// Call refresh function after successful save
					if (onRecordAdded) {
						onRecordAdded();
					}
					// Open task entry modal with prefilled connection data
					setTaskPrefilledData({
						threads: recordFormData.threadIDs && recordFormData.threadIDs.length > 0 ? recordFormData.threadIDs : '',
						connID: recordFormData.connID,
						contractIDs: recordFormData.contractIDs || [],
						connectionLocked: true,
						taskLabel: 'Next Task',
						hideDescription: true,
						descriptionLabel: 'Task Description',
						typeLabel: 'Type',
						pocUserLabel: 'Assigned to',
						threadsLabel: `Linked ${currActiveThreadType.typPlr}`,
						contractsLabel: 'Linked Contracts',
						secConnIDs: recordFormData.secConnIDs || [],
					});
					// Close the record entry dialog
					setOpen(false);
					// Now open task entry after a short delay to ensure state is updated
					setTimeout(() => {
						setIsThreadEntryOpen(true);
					}, 100);
					return;
				}

				setOpen(false);
				if (selectedTaskForRecord) {
					toast.success("Task completed.")
				} else if (isNextAction) {
					toast.success("Record and task added.");
				} else {
					toast.success("Record added.");
				}
				// Call refresh function after successful save
				if (onRecordAdded) {
					onRecordAdded();
				}

				if ([-1, -2].includes(recordFormData.currStageID) && onCloseThread) {
					onCloseThread();
				}

				if (recordFormData.currStageID == -3) {
					const threadObj = currentThread || (threadData?.find(t => t.ThreadID == (currentThreadID || (recordFormData.threadIDs || [])[0])));
					const snapshotPrefill = {
						connID: recordFormData.connID,
						inquiryThreadID: currentThreadID || (recordFormData.threadIDs || [])[0],
						contactPerson: threadObj?.connectionData?.ContactName || threadObj?.ContactPerson || prefilledData?.contactName || '',
						product: threadObj?.MainProduct || '',
						model: threadObj?.Model || '',
						reference: threadObj?.Reference || '',
						quantity: threadObj?.MainQty || '',
						businessValue: threadObj?.BizProspect || 0,
						notes: threadObj?.Notes || '',
						source: threadObj?.Source || '',
						pocUserID: threadObj?.POCUserID || recordFormData.pocUserID,
						sinceDT: threadObj?.SinceDT || '',
					};
					setConvertToSalePrefill(snapshotPrefill);
					setIsThreadEntryOpen(true);
				}
				return;
			}
			toast.error(result.message);
		} catch (error) {
			toast.error(error.message);
			console.error("Error saving record:", error);
		}
	}

	// Function for handling direct task close
	const handleCloseTask = async () => {
		try {
			const result = await taskActionAPI(selectedTaskForRecord, 'COMPLETE');
			if (!result.error) {
				toast.success("Task completed.");
				setOpen(false);
				return;
			}
			toast.error(result.message);

		} catch (error) {
			console.error(`Error while closing task:`, error);
			toast.error(error.message);
		}
	}

	const handleDeleteRecord = async () => {
		if (!recordFormData.recordID) {
			toast.error("No record ID found for deletion");
			return;
		}

		try {
			await manageRecordAPI({
				recID: recordFormData.recordID,
				connID: recordFormData.connID,
				pocUserID: recordFormData.pocUserID,
				recDT: recordFormData.recordDate,
				recType: recordFormData.recordType,
				recNotes: recordFormData.notes,
				recThreadIDs: recordFormData.threadIDs.join(','),
				recContractIDs: recordFormData.contractIDs.join(','),
				recSecConnIDs: recordFormData.secConnIDs.join(',')
			}, null, 'DELETE', 5);

			toast.success("Record deleted.");
			if (onRecordAdded) {
				// Pass deletion info so parent can remove this record from list
				onRecordAdded({ isDeleted: true, recordID: recordFormData.recordID });
			}
			setOpen(false);
			setIsDeleteDialogOpen(false);
		} catch (error) {
			console.error(`Error deleting record:`, error);
			toast.error(error.message || "Failed to delete record");
			setIsDeleteDialogOpen(false);
		}
	}

	// Function to render disabled connection field in mobile
	const renderDisableConn = () => {
		const isShow = isMobile && recordFormData.connID && prefilledData?.connectionLocked;
		const selectedConnection = connectionData.find(conn => Number(conn.ConnID || conn.NodeID || 0) === Number(recordFormData.connID));
		const businessName = selectedConnection?.BizName || selectedConnection?.ContactName || prefilledData?.bizName || prefilledData?.contactName || 'Loading...';

		return isShow ? (
			<div key='connID' className="space-y-1.5">
				<Label htmlFor='connID' required={true}>
					Contact
				</Label>
				<Input
					id='connID'
					name='connID'
					value={businessName}
					onChange={() => { }} // No-op since it's disabled
					placeholder={'Contact'}
					className="h-11 text-sm transition-all duration-200 bg-white sahi-input"
					type={'text'}
					autoComplete={'off'}
					maxLength={50}
					disabled={true}
				/>
			</div>
		) : null;
	};

	// Datepicker component 
	const getDatePicker = (disabled = false) => {
		return <>
			<div className="w-full">
				<DatePicker
					value={recordFormData.recordDate}
					onChange={(value) => handleFieldChange("recordDate", format(value, "dd-MMM-yy"))}
					placeholder="Select Date"
					label={"Date"}
					param="noFuture"
					required
					disabled={disabled}
				/>
			</div>
		</>
	}

	// TimePicker component 
	const getTimePicker = (disabled = false) => {
		return <>
			<div className="w-full">
				<TimeInputWithDialog
					fieldId="recordTime"
					value={recordFormData.recordTime}
					onChange={(value) => handleFieldChange("recordTime", value)}
					date={recordFormData.recordDate}
					placeholder="Select Time"
					label={"Time"}
					required
					disabled={disabled}
				/>
			</div>
		</>
	}

	const getMultiSelectSecConns = () => {
		// Only show if one thread is selected and secondaryContacts are available for that thread
		if (recordFormData.threadIDs.length !== 1 || secondaryContacts.length === 0) return null;

		return (
			<div key="secConnIDs" className="space-y-1.5">
				<Label required={false}>Secondary Contacts</Label>
				<MultiSelect
					variant="default"
					options={secondaryContacts}
					onValueChange={(values) => handleFieldChange('secConnIDs', values)}
					defaultValue={recordFormData.secConnIDs}
					placeholder="Select Contacts"
					emptyMessage="No contacts found."
					animation={2}
					maxCount={50}
					isSelectAll={false}
					isSearch={true}
					closeIcon={false}
					outsideLabel={true}
					selectWidth={isMobile ? '100%' : '50%'}
					label='contacts'
					// Disable if thread is locked and we are coming from a prefilled task
					disabled={prefilledData?.threadLocked}
				/>
			</div>
		);
	}

	const getConnectionSearchableDropdown = () => {
		return <>
			<CustomSearchableDropdown
				options={getFormatedArray("connID")}
				value={recordFormData.connID}
				onSelect={(value, option, searchText) => handleSearchSelectChange("connID", option, value, searchText)}
				placeholder="Select Contact"
				searchPlaceholder="Search Contacts"
				displayKey="label"
				label={`Contact`}
				valueKey="value"
				allowAdd={!prefilledData.connectionLocked && canAddConnections}
				addText="New Contact"
				emptyMessage="No contacts found."
				disabled={prefilledData.connectionLocked}
				renderOption={(option) => (
					<div className="flex flex-col items-start w-full min-w-0">
						<span className="truncate block w-full">{option.label}</span>
						{/* <span className="text-sm text-gray-500">
							{option.connection.ContactName} ({option.connection.Mobile})
						</span> */}
					</div>
				)}
				showAddInHeader={true}
				mobileDropdownText="Select Contact"
			/> </>
	}

	// Dropdown component
	const getDropDown = (fieldID, required, label, placeholder, data = [], searchEnabled = true, disabled = false) => {
		// Special handling for recordType field with icons
		if (fieldID === 'recordType') {
			const ALLOWED_RECORD_TYPES = {
				Call: 'Phone',
				Message: 'MessageCircle',
				Meeting: 'Handshake',
				Visit: 'MapPin'
			};

			const predefinedTypes = Object.keys(ALLOWED_RECORD_TYPES)
				.filter(type =>
					data.some(dt => dt.value === type)
				)
				.map(type => ({
					value: type,
					icon: ALLOWED_RECORD_TYPES[type]
				}));

			const isPredefinedType = (value) => {
				return predefinedTypes.some(type => type.value === value);
			};

			const handleIconClick = (value) => {
				if (disabled) return;
				const option = data.find(opt => opt.value === value);
				if (option) {
					handleSearchSelectChange(fieldID, option, value);
					handleFieldChange(fieldID, value);
				}
			};

			const handleDropdownSelect = (value) => {
				if (value === "add") {
					setRecordTypeDialogOpen(true);
					return;
				}
				const option = data.find(opt => opt.value === value);
				if (option) {
					handleSearchSelectChange(fieldID, option, value);
					handleFieldChange(fieldID, value);
				}

				// Only hide dropdown if the selected type is a predefined type AND dropdown was opened by More button
				if (isPredefinedType(value) && showDropdownRecordType) {
					setShowDropdownRecordType(false);
				}
			};

			const handleMoreClick = () => {
				if (disabled) return;
				setShowDropdownRecordType(true);
			};

			const getIcon = (iconName) => {
				const icons = {
					Phone: <Phone className="h-4 w-4" />,
					MessageCircle: <BsWhatsapp className="h-4 w-4" />,
					Handshake: <Handshake className="h-4 w-4" />,
					MapPin: <MapPin className="h-4 w-4" />,
					MoreHorizontal: <MoreHorizontal className="h-4 w-4" />
				};
				return icons[iconName] || null;
			};

			// Determine if the currently selected value is one of the icon types
			const isValueInIcons = predefinedTypes.some(type => type.value === recordFormData[fieldID]);

			// Show icons only if icons exist AND user hasn't toggled dropdown AND (no value selected OR selected value is an icon)
			const shouldShowIcons = predefinedTypes.length > 0 && !showDropdownRecordType && (!recordFormData[fieldID] || isValueInIcons);

			return (
				<div className='space-y-1.5'>
					<Label htmlFor={fieldID} className={"cursor-pointer mb-1"} required={required}>
						{label}
					</Label>

					<div className={`grid w-full items-start h-full gap-2 ${shouldShowIcons ? 'grid-cols-[1fr_auto]' : 'grid-cols-1'}`}>
						{shouldShowIcons ? (
							<>
								<div
									className="grid gap-2"
									style={{ gridTemplateColumns: `repeat(${predefinedTypes.length}, minmax(0, 1fr))` }}
								>
									{predefinedTypes.map((type) => (
										<TooltipProvider key={type.value}>
											<Tooltip delayDuration={300}>
												<TooltipTrigger asChild>
													<div className="flex flex-col items-center gap-1">
														<button
															type="button"
															onClick={() => handleIconClick(type.value)}
															className={`flex items-center justify-center h-11 w-full rounded-md border transition-colors ${recordFormData[fieldID] === type.value
																? 'border-sahi-primary-color bg-white text-sahi-primary-color'
																: 'border-input bg-white md:hover:border-gray-400'
																} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
															disabled={disabled}
														>
															{getIcon(type.icon)}
														</button>
														{recordFormData[fieldID] === type.value && (
															<span className="text-[10px] text-sahi-primary-color font-medium">
																{type.value}
															</span>
														)}
													</div>
												</TooltipTrigger>
												<TooltipContent side="top" align="center" className="text-sm">
													<p>{type.value}</p>
												</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									))}
								</div>
								<TooltipProvider>
									<Tooltip delayDuration={300}>
										<TooltipTrigger asChild>
											<button
												type="button"
												onClick={() => {
													setShowDropdownRecordType(true);
													setAutoOpenDropdownRecordType(true);
												}}
												className={`flex items-center justify-center h-11 w-11 rounded-md border border-input bg-white md:hover:border-gray-400 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
												disabled={disabled}
											>
												{getIcon('MoreHorizontal')}
											</button>
										</TooltipTrigger>
										<TooltipContent side="top" align="center" className="text-sm">
											<p>More Options</p>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</>
						) : (
							<CustomSearchableDropdown
								fieldID={fieldID}
								options={data}
								value={recordFormData[fieldID]}
								onSelect={(val) => {
									handleDropdownSelect(val);
									setAutoOpenDropdownRecordType(false);
								}}
								placeholder={placeholder}
								cancellable={false}
								openDialog={autoOpenDropdownRecordType}
								onClose={() => {
									setShowDropdownRecordType(false);
									setAutoOpenDropdownRecordType(false);
								}}
								allowAdd={isAdmin}
								addText="Add New Type"
								disabled={disabled}
							/>
						)}
					</div>
				</div>
			);
		}

		// Default dropdown rendering for other field types
		return (
			<>
				<Label htmlFor={fieldID} required={required}>
					{label}
				</Label>
				<CustomSearchableDropdown
					fieldID={fieldID}
					required={false}
					label={label}
					placeholder={placeholder}
					options={data}
					value={recordFormData[fieldID]}
					onChange={(val) => handleFieldChange(fieldID, val)}
					emptyMessage={<>
						Please add types in settings.
					</>}
					onSelect={(val, option, searchText) => {
						if (val === "add" && fieldID === "recordType") {
							setRecordTypeDialogOpen(true);
						} else {
							handleSearchSelectChange(fieldID, option, val, searchText);
						}
					}}
					searchEnabled={searchEnabled}
					allowAdd={fieldID === "recordType" && isAdmin}
					disabled={disabled}
					addText="Add New Type"
				/>
			</>
		);
	}

	// CheckBox component
	const getCheckBox = (fieldID, label, isPadding = true) => {
		const id = fieldID == -1 ? 'nextAction' : fieldID;
		return <>
			<div className={`flex items-center space-x-2 ${isPadding ? 'lg:pt-6' : ''} pt-0`}>
				<Checkbox
					id={id}
					checked={fieldID == -1 ? isNextAction : recordFormData[fieldID]}
					onCheckedChange={fieldID == -1 ? () => { setIsNextAction(!isNextAction) } : (checked) => handleFieldChange(fieldID, checked)}
					className="border-gray-300"
				/>
				<Label htmlFor={id} className={"cursor-pointer"}>
					{label}
				</Label>
			</div>
		</>
	}

	// Multiple Selection Threads/Contracts Component
	const getMultiSelectThreads = (fieldID, label, placeholder, options, isDisable = false) => {
		return <>
			<Label required={false}>{isNextAction ? `Linked ${label}` : `Linked ${label}`}</Label>
			<MultiSelect
				variant="default"
				options={options}
				onValueChange={(values) => handleFieldChange(fieldID, values)}
				defaultValue={recordFormData[fieldID]}
				placeholder={placeholder}
				emptyMessage={`No ${label} found.`}
				animation={2}
				maxCount={50}
				isSelectAll={false}
				isSearch={false}
				closeIcon={false}
				disabled={isDisable}
			/>
		</>
	}

	return (
		<>
			<Dialog open={open} onOpenChange={setOpen}>
				{triggerButton && (
					<DialogTrigger asChild>
						<Button variant="default">Add Record</Button>
					</DialogTrigger>
				)}
				<DialogContent onInteractOutside={(e) => e.preventDefault()} className={`${((isNextAction || isUpdateStatus) && !shouldOpenTaskEntry) ? 'lg:!max-w-[56vw]' : 'lg:!max-w-[50vw]'} max-w-[100vw]`}>
					<DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
						<div className="sm:hidden flex items-center justify-between w-full h-8">
							<div className="flex items-center gap-2 flex-1 min-w-0">
								<DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
									<ArrowLeft className="h-6 w-6" />
									<span className="sr-only">Back</span>
								</DialogClose>
								<DialogTitle className="text-lg font-medium">
									<div className="truncate">
										{getMobileHeaderTitle(isEditMode ? `${recordFormData.recordType} Detail Update` : "Record Entry")}
									</div>
								</DialogTitle>
							</div>
							<div className="flex items-center gap-2 flex-shrink-0">
								{isEditMode && (
									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="h-8 w-8 p-0 sm:hover:bg-gray-200"
										onClick={() => setIsDeleteDialogOpen(true)}
									>
										<Trash2 className="text-text-color h-4 w-4" />
									</Button>
								)}
							</div>
						</div>
						<div className="hidden sm:flex items-center justify-between w-full gap-2 min-w-0">
							<DialogTitle className="text-lg md:text-xl flex items-center gap-1 md:gap-2 flex-1 min-w-0">
								<span className="truncate font-semibold">
									{isEditMode
										? `${recordFormData.recordType} Detail Update`
										: (() => {
											const lockedName =
												prefilledData?.bizName ||
												prefilledData?.contactName ||
												prefilledData?.agencyName ||
												prefilledData?.name ||
												connectionData?.find((c) => c.ConnID === prefilledData?.connID)?.BizName ||
												connectionData?.find((c) => c.ConnID === prefilledData?.connID)?.ContactName;
											return prefilledData?.connectionLocked && lockedName
												? `Record Entry - ${lockedName}`
												: `Record Entry`;
										})()}
								</span>

								{prefilledData.connectionLocked && prefilledData?.connID && (
									<button
										type="button"
										onClick={(e) => {
											e.stopPropagation();
											setIsDetailOpen(true);
										}}
										className="ml-1 transition"
									>
										<SquareArrowOutUpRight className="h-5 w-5 text-text-color" />
									</button>
								)}
							</DialogTitle>

							<ConnectionDetailsWrapper
								isOpen={isDetailOpen}
								onClose={() => setIsDetailOpen(false)}
								connID={prefilledData.connID}
							/>

							<div className="flex items-center gap-2 flex-shrink-0">
								{isEditMode && (
									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="h-8 w-8 p-0 sm:text-text-color sm:hover:bg-gray-200"
										onClick={() => setIsDeleteDialogOpen(true)}
									>
										<Trash2 className="h-4 w-4" />
									</Button>
								)}
								<DialogClose className="flex-shrink-0" tabIndex={-1}>
									<X className="h-5 w-5" />
									<span className="sr-only">Close</span>
								</DialogClose>
							</div>
						</div>
					</DialogHeader>
					<div className="px-4 flex-1 overflow-y-auto max-h-[calc(100vh-100px)] sm:max-h-[80vh]">
						<div className="space-y-4">
							{renderDisableConn()}
							<div key="notes" className="space-y-1.5">
								<Label htmlFor="notes" required={true}>
									Record Notes
								</Label>

								<div className="relative group">
									<Textarea
										id="notes"
										name="notes"
										maxLength={1000}
										value={recordFormData.notes}
										onChange={(e) => handleFieldChange("notes", e.target.value)}
										placeholder="e.g. Discussed contract details."
										className="min-h-[80px] text-sm resize-none pr-20 focus-visible:ring-1 transition-all"
									/>
									<SpeechControls
										targetId="notes"
										value={recordFormData.notes}
										onChange={(val) => handleFieldChange("notes", val)}
										isMobile={isMobile}
									/>

									{/* Geo Location Tag within Textarea */}
									{!isEditMode && showLocation && (
										<div className="absolute right-2 bottom-2 bg-white/90 backdrop-blur-sm rounded-sm px-1 shadow-sm border">
											{getCheckBox('isLocationReq', 'Tag Geo', false)}
										</div>
									)}
								</div>
							</div>
							{!prefilledData.connectionLocked ? (
								<>
									<div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-1">
										<div key="connection" className="space-y-1.5">
											<Label required={true}>Contact</Label>
											{getConnectionSearchableDropdown()}
										</div>
										<div key="recordType" className="space-y-1.5">
											{getDropDown("recordType", true, "Type", "Select Type", recordTypes, true)}
										</div>
										<div className="grid grid-cols-2 gap-4 lg:col-span-2">
											<div className="space-y-1.5">
												{getDatePicker()}
											</div>
											<div className="space-y-1.5">
												{getTimePicker()}
											</div>
										</div>
									</div>
									{(isAdmin && !isSmartSeller) && (
										<div className="space-y-1.5">
											{getDropDown("pocUserID", true, "Done by", "Select Executive", getFormatedArray("pocUserID"))}
										</div>
									)}
								</>
							) :
								(<div className={`mb-1 grid grid-cols-1 lg:grid-cols-4 gap-4`}>
									{/* Type */}
									<div className="space-y-1.5">
										{getDropDown("recordType", true, "Type", "Select Type", recordTypes, true, isEditMode)}
									</div>

									{/* Date + Time */}
									<div className="grid grid-cols-2 gap-4 lg:col-span-2">
										<div className="space-y-1.5">
											{getDatePicker(isEditMode)}
										</div>
										<div className="space-y-1.5">
											{getTimePicker(isEditMode)}
										</div>
									</div>

									{/* Done by (for Admin and SmartSeller) */}
									{(isAdmin && !isSmartSeller) && (
										<div className="space-y-1.5">
											{getDropDown("pocUserID", true, "Done by", "Select Executive", getFormatedArray("pocUserID"), true, isEditMode)}
										</div>
									)}

									{/* Empty div to maintain grid structure when isNextAction is true */}
									{isNextAction && (
										<div className="space-y-1.5">
											{/* Empty space for visual balance */}
										</div>
									)}
								</div>
								)}

							{/* Handle Admin Block and Thread Selection - Only show in ADD mode */}
							{!isEditMode && (((allowLinkedThreads || allowLinkedCtrcs) && filteredThreads.length > 0 && selectedTaskForRecord?.threads?.length !== 0)
								|| (showModule.contracts && contracts.length > 0)) && (
									<div className={`grid lg:grid-cols-2 gap-4 ${isNextAction ? '!mt-0' : ''}`}>
										{(allowLinkedThreads && filteredThreads.length > 0 && selectedTaskForRecord?.threads?.length !== 0) && (
											<div key="thread" className="space-y-1.5">
												{getMultiSelectThreads('threadIDs', `${currActiveThreadType.typPlr}`, `Select ${currActiveThreadType.typPlr}`, filteredThreads, isThreadSelectDisabled)}
											</div>
										)}

										{(allowLinkedCtrcs && showModule.contracts && contracts.length > 0) && (
											<div key="ContractID" className="space-y-1.5">
												{getMultiSelectThreads("contractIDs", 'Contracts', 'Select Contracts', getFormatedArray("ContractIDs"), false)}
											</div>
										)}
									</div>
								)}

							{!isEditMode && (allowSecConns) && getMultiSelectSecConns()}

							{!isEditMode && (selectedTaskForRecord || showUpdateStatus) ? (
								// Only show Update Status when a task is selected for record (i.e., we're recording against an existing task)
								<>
									{availableThreadOptions.length > 0 && (
										<div className="space-y-4">
											<div className="flex items-center space-x-2">
												<Checkbox
													id="updateStatus"
													checked={isUpdateStatus}
													onCheckedChange={(checked) => setIsUpdateStatus(checked)}
													className="border-gray-300"
												/>
												<Label htmlFor="updateStatus" className="cursor-pointer font-semibold">
													Update {isInqOnlyMode ? 'Inquiry' : 'Thread'} Status
												</Label>
											</div>

											{isUpdateStatus && (
												<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
													{/* 1. Select Thread Dropdown (Only shown if multiple threads available) */}
													{availableThreadOptions.length > 1 && (
														<div className="space-y-1.5 w-full">
															{/* <Label required={true}>Select {isInqOnlyMode ? 'Inquiry' : 'Thread'}</Label> */}
															<CustomSearchableDropdown
																fieldID="threadIDs"
																placeholder={`Select ${isInqOnlyMode ? 'Inquiry' : 'Thread'}`}
																options={availableThreadOptions}
																value={recordFormData.threadIDs?.[0]} // Expects single value for this dropdown
																onSelect={(val) => handleFieldChange('threadIDs', [val])} // Pass as array for threadIDs state
																searchEnabled={true}
																emptyMessage="No open threads found."
																noSort={true}
															/>
														</div>
													)}

													{/* 2. Select Stage Dropdown */}
													{(availableThreadOptions.length === 1 || recordFormData.threadIDs.length === 1) && (
														<div className={`space-y-1.5 w-full`}>
															<CustomSearchableDropdown
																fieldID="currStageID"
																placeholder="Select Status"
																options={getCurrentThreadStages()}
																value={recordFormData.currStageID}
																onSelect={(val) => handleFieldChange('currStageID', val)}
																searchEnabled={true}
																emptyMessage="No stages found."
																noSort={true}
																label="Select Status"
															/>
														</div>
													)}

													{/* 3. Select Reason Dropdown */}
													{recordFormData.currStageID === -1 && (
														<div className={`space-y-1.5 w-full`}>
															<CustomSearchableDropdown
																fieldID="reason"
																placeholder="Select Reason"
																options={getReasonOptions()}
																value={recordFormData.reason}
																onSelect={(val) => handleFieldChange('reason', val)}
																searchEnabled={true}
																emptyMessage="No reasons found."
															/>
														</div>
													)}
												</div>
											)}
										</div>
									)}
								</>
							) : null
							}

							{/* Mark as Cold Section - Show only when Mark Fake or Mark Rejected is selected */}
							{!isEditMode && isUpdateStatus && recordFormData.currStageID === -1 && (
								<div className="space-y-2">
									<div className="flex items-center space-x-2">
										<Checkbox
											id="isMarkCold"
											checked={recordFormData.isMarkCold}
											onCheckedChange={(checked) => {
												handleFieldChange("isMarkCold", checked);
												setShowColdDatePicker(checked);
												if (!checked) {
													handleFieldChange("markColdDT", "");
												}
											}}
											className="border-gray-300"
										/>
										<Label htmlFor="isMarkCold" className="cursor-pointer">
											Mark as Cold
										</Label>
									</div>
								</div>
							)}

							{/* Next Task Section - Show only when not opened from a task */}
							{!selectedTaskForRecord && (
								<div className="space-y-4">
									<div className="flex items-center space-x-2">
										<Checkbox
											id="isNextAction"
											checked={isNextAction}
											onCheckedChange={(checked) => {
												setIsNextAction(checked);
												// Reset task data when unchecked
												if (!checked) {
													setTaskPrefilledData({});
													setIsTaskEntryOpenAfterSave(false);
												} else {
													if (shouldOpenTaskEntry) {
														// Don't show task fields immediately, flag to open after record save
														setIsTaskEntryOpenAfterSave(true);
														setTaskPrefilledData({
															threads: recordFormData.threadIDs && recordFormData.threadIDs.length > 0 ? recordFormData.threadIDs : '',
															connID: recordFormData.connID || '',
															contractIDs: recordFormData.contractIDs || [],
															connectionLocked: true,
															taskLabel: 'Next Task',
															hideDescription: true,
															descriptionLabel: 'Task Description',
															typeLabel: 'Type',
															pocUserLabel: 'Assigned to',
															threadsLabel: `Linked ${currActiveThreadType.typPlr}`,
															contractsLabel: 'Linked Contracts',
															secConnIDs: recordFormData.secConnIDs || [],
														});
													} else {
														// FORCE immediate prefill when checking (original behavior)
														setTaskPrefilledData({
															threads: recordFormData.threadIDs && recordFormData.threadIDs.length > 0 ? recordFormData.threadIDs : '',
															connID: recordFormData.connID || '',
															contractIDs: recordFormData.contractIDs || [],
															connectionLocked: true,
															taskLabel: 'Next Task',
															hideDescription: true,
															descriptionLabel: 'Task Description',
															typeLabel: 'Type',
															pocUserLabel: 'Assigned to',
															threadsLabel: `Linked ${currActiveThreadType.typPlr}`,
															contractsLabel: 'Linked Contracts',
															secConnIDs: recordFormData.secConnIDs || [],
														});
													}
												}
											}}
											className="border-gray-300"
										/>
										<Label htmlFor="isNextAction" className="cursor-pointer font-semibold">
											Add Next Task
										</Label>
									</div>

									{isNextAction && !shouldOpenTaskEntry && (
										<TaskEntry
											ref={formRef}
											key={`task-entry-${isNextAction}`}
											open={isNextAction}
											triggerButton={false}
											prefilledData={taskPrefilledData}
											extFieldUse={true}
										/>
									)}
								</div>
							)}

							{/* NOTE: Commented out - Previous conditional TaskEntry render (kept for reference)
						{isEditMode && isNextAction && (
							<TaskEntry
								ref={formRef}
								open={isNextAction}
								triggerButton={false}
								prefilledData={taskPrefilledData}
								extFieldUse={true}
							/>
						)}
						*/}

						</div>
						<div className="py-4">
							<div className="w-full flex flex-col gap-4 sm:flex-row sm:justify-start sm:gap-3">
								<Button onClick={handleSaveRecord} className="w-full sm:w-auto">
									<Check className="h-4 w-4" strokeWidth={3} />
									{isEditMode ? 'Update Record' : (isNextAction ? 'Save' : (selectedTaskForRecord ? 'Save & Close Task' : 'Save Record'))}
								</Button>
								{!isMobile ? <Button variant="secondary" className="w-full sm:w-auto" onClick={() => setOpen(false)}>
									<X className="h-5 w-5" />
									Close
								</Button> : null}
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Delete confirmation dialog */}
			<AlertBox
				open={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
				title="Delete Record"
				content={
					<div>
						<p className="mb-2">Are you sure you want to delete this record?</p>
						<p className="text-sm text-sub-text-color">This action cannot be undone.</p>
					</div>
				}
				actions={[
					{
						label: "Cancel",
						variant: "cancel",
						value: "cancel",
						className: "w-full sm:w-auto",
						icon: <X className="h-4 w-4" />
					},
					{
						label: "Delete",
						variant: "action",
						value: "delete",
						className: "w-full sm:w-auto",
						icon: <Trash2 className="h-4 w-4" />
					}
				]}
				onAction={(actionValue) => {
					if (actionValue === "delete") {
						handleDeleteRecord();
					} else {
						setIsDeleteDialogOpen(false);
					}
				}}
			/>

			{/* Open TaskEntry after record is saved when shouldOpenTaskEntry is true */}
			{shouldOpenTaskEntry && isThreadEntryOpen && (
				<TaskEntry
					triggerButton={false}
					open={isThreadEntryOpen}
					onOpenChange={(open) => {
						setIsThreadEntryOpen(open);
					}}
					prefilledData={taskPrefilledData}
					dialogTitle="Next Task Entry"
				/>
			)}
			{/* Connection entry dialog box */}
			<ConnectionEntryDialog
				triggerButton={false}
				open={connectionDialogOpen}
				onOpenChange={(open) => {
					setConnectionDialogOpen(open)
					if (!open) {
						// Reopen taskEntry dialog when connection dialog closes
						setOpen(true)
						setConnectionSearchText('');
					}
				}}
				parentDialogSetter={open}
				prefilledData={connectionSearchText ? { businessName: connectionSearchText } : null}
				// onConnectionAdded={(savedConnection) => {
				// 	// This sets the newly added connection as selected in the form
				// 	setRecordFormData((prev) => ({
				// 		...prev,
				// 		conn: savedConnection.ConnID,
				// 	}))
				// 	// Close connection dialog and reopen thread dialog
				// 	setConnectionDialogOpen(false)
				// 	setOpen(true)
				// }}
				onConnectionAdded={(savedConnection) => {
					if (savedConnection) {
						const connID = savedConnection.ConnID || savedConnection.connID;

						// Set the connection ID in form data
						setRecordFormData(prev => ({
							...prev,
							connID: connID
						}));

						// Force re-render to ensure dropdown shows the new selection
						setTimeout(() => {
							setRecordFormData(prev => ({
								...prev,
								connID: connID
							}));
						}, 100);
					}

					// Close connection dialog and reopen task dialog
					setConnectionDialogOpen(false);
					setOpen(true);
				}}
				calledFrom="REC"
				isBasic={true}
			/>
			{/* Record type entry dialog box */}
			{recordTypeDialogOpen && (
				<MastersDialog
					open={recordTypeDialogOpen}
					onClose={() => {
						setRecordTypeDialogOpen(false)
					}}
					masterType="recordTypes"
					directAdd={true}
					openEntry={true}
					onProductAdded={handleRecordTypeAdded}
				/>
			)}

			<ThreadEntry
				triggerButton={false}
				open={isThreadEntryOpen && !shouldOpenTaskEntry}
				onOpenChange={(open) => {
					setIsThreadEntryOpen(open);
					// Close the parent modal when this dialog closes/finishes
					if (!open && onCloseThread) {
						onCloseThread();
					}
				}}
				onThreadUpdated={() => {
					setIsThreadEntryOpen(false);
					if (onCloseThread) onCloseThread();
				}}
				threadTypeID={2}
				calledFrom={isInqOnlyMode ? 'INQDONE' : ''}
				preselectedConnID={convertToSalePrefill?.connID || recordFormData.connID}
				prefilledData={convertToSalePrefill || {
					connID: recordFormData.connID || currentThread?.ConnID,
					inquiryThreadID: currentThreadID || currentThread?.ThreadID,
					contactPerson: currentThread?.connectionData?.ContactName || currentThread?.connectionData?.contactName || currentThread?.ContactPerson || currentThread?.contactPerson || prefilledData?.contactName,
					product: currentThread?.MainProduct || currentThread?.mainProduct || currentThread?.product || currentThread?.Product,
					model: currentThread?.Model || currentThread?.model,
					reference: currentThread?.Reference || currentThread?.reference,
					quantity: currentThread?.MainQty || currentThread?.mainQty || currentThread?.quantity,
					businessValue: currentThread?.BizProspect ?? currentThread?.bizProspect ?? currentThread?.businessValue,
					notes: currentThread?.Notes || currentThread?.notes,
					source: currentThread?.Source || currentThread?.source,
					pocUserID: currentThread?.POCUserID || currentThread?.pocUserID,
					...((currentThread?.SinceDT || currentThread?.sinceDT) && { sinceDT: currentThread?.SinceDT || currentThread?.sinceDT }),
				}}
			/>
		</>
	)
}
